/**
 * 设备测试面板包装组件
 * v8.2 新增：复用 TestPanel，处理 Token 获取和刷新
 */

import { useState, useEffect, useCallback } from 'react'
import { Loader2, RefreshCw, XCircle } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import type { Device } from '@/modules/agent/types/device'
import { DeviceApiService } from '@/modules/agent/services/device-api'

// 动态导入 TestPanel（避免循环依赖）
// TestPanel 在 ProjectStudio.tsx 中定义，需要导出
import { TestPanel } from '@/modules/agent/pages/ProjectStudio'

interface DeviceTestPanelProps {
  device: Device
  projectName: string
  onClose: () => void
}

export function DeviceTestPanel({ device, projectName, onClose }: DeviceTestPanelProps) {
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // 获取测试 Token
  const fetchToken = useCallback(async () => {
    setLoading(true)
    setError(null)
    console.log('[DeviceTestPanel] 获取测试 Token...')
    
    try {
      // 30 分钟有效期
      const response = await DeviceApiService.getTestToken(device.project_id, device.device_id, 1800)
      if (response.data?.token) {
        console.log('[DeviceTestPanel] ✅ Token 获取成功')
        setToken(response.data.token)
      } else {
        throw new Error('响应中没有 token')
      }
    } catch (err) {
      console.error('[DeviceTestPanel] ❌ 获取 Token 失败:', err)
      setError(err instanceof Error ? err.message : '获取测试 Token 失败')
    } finally {
      setLoading(false)
    }
  }, [device.project_id, device.device_id])

  // Token 刷新函数（供 TestPanel 使用）
  const tokenGetter = useCallback(async (): Promise<string | null> => {
    console.log('[DeviceTestPanel] 🔑 Token 过期，重新获取...')
    try {
      const response = await DeviceApiService.getTestToken(device.project_id, device.device_id, 1800)
      if (response.data?.token) {
        console.log('[DeviceTestPanel] ✅ 新 Token 获取成功')
        setToken(response.data.token)
        return response.data.token
      }
      console.error('[DeviceTestPanel] ❌ Token 响应中没有 token')
      return null
    } catch (err) {
      console.error('[DeviceTestPanel] ❌ 获取 Token 失败:', err)
      return null
    }
  }, [device.project_id, device.device_id])

  // 初始化获取 Token
  useEffect(() => {
    fetchToken()
  }, [fetchToken])

  // 加载状态
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20">
        <Loader2 className="h-8 w-8 text-violet-500 animate-spin mb-4" />
        <p className="text-sm text-slate-600">正在获取测试 Token...</p>
        <p className="text-xs text-slate-400 mt-1">设备: {device.device_id}</p>
      </div>
    )
  }

  // 错误状态
  if (error || !token) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20">
        <XCircle className="h-10 w-10 text-red-400 mb-4" />
        <p className="text-sm text-red-600 font-medium mb-2">获取测试 Token 失败</p>
        <p className="text-xs text-slate-500 mb-4 max-w-xs text-center">{error}</p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchToken}
            className="text-xs"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            重试
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-xs"
          >
            关闭
          </Button>
        </div>
      </div>
    )
  }

  // 渲染 TestPanel（设备测试模式）
  return (
    <TestPanel
      projectId={device.project_id}
      projectName={projectName}
      mode="device"
      deviceTestConfig={{
        deviceId: device.device_id,
        deviceName: device.name || device.device_id,
        externalToken: token,
        tokenGetter,
      }}
      onClose={onClose}
      // 设备测试不需要这些参数
      agents={[]}
      routers={[]}
      edges={[]}
      isSaved={true}
    />
  )
}
